import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import toast from "react-hot-toast";
import apiService from "services/api";

const PAGE_SIZE = 20;
const IMG_BASE = process.env.REACT_APP_IMG || "";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function formatShortDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN");
}

function normalizePhone(phone) {
  if (!phone) return "";
  return String(phone).replace(/\D/g, "").replace(/^0+/, "");
}

function getWhatsAppUrl(phone, message) {
  const digits = normalizePhone(phone);
  if (!digits) return null;
  const withCountry = digits.length === 10 ? `91${digits}` : digits;
  const base = `https://wa.me/${withCountry}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

function buildOrderText(row) {
  const member = row.originalMemberId || {};
  const planName = row.plan?.name || row.hmtPlanId?.plan?.name || "—";
  const amount = row.plan?.amount ?? row.hmtPlanId?.plan?.amount ?? "—";
  const thali =
    row.thaliCount ?? row.plan?.thaliCount ?? row.hmtPlanId?.plan?.thaliCount ?? "—";
  const phone = member.phone ? `+91${member.phone}` : "—";

  return [
    "ApnaThali HMT Order",
    `Order ID: ${row._id || "—"}`,
    `Customer: ${member.name || "—"}`,
    `Phone: ${phone}`,
    `Plan: ${planName}`,
    `Amount: ₹${amount}`,
    `Thali: ${thali}`,
    `Slot: ${row.slot || "—"}`,
    `Address: ${getCustomerAddress(row)}`,
    `Start: ${formatShortDate(row.startDate)}`,
    `End: ${formatShortDate(row.endDate)}`,
    `Payment: ${row.paymentStatus ? "Paid" : "Pending"}`,
    `Status: ${row.hmtPlanId?.status || "—"}`,
    `Transaction: ${row.transactionId || row.hmtPlanId?.transactionId || "—"}`,
    `Date: ${formatDate(row.createdAt)}`,
  ].join("\n");
}

function getCustomerAddress(row) {
  return row.address || row.hmtPlanId?.address || "—";
}

function getInvoiceUrl(row) {
  const hmt = row.hmtPlanId;
  if (hmt?.invoiceUrl) return hmt.invoiceUrl;
  if (hmt?.invoiceFileName) return `${IMG_BASE}${hmt.invoiceFileName}`;
  return null;
}

/** Cell wrapper so content stacks vertically inside the shared Table component */
function CellStack({ children }) {
  return (
    <SoftBox component="span" display="block" sx={{ minWidth: 120, py: 0.5 }}>
      {children}
    </SoftBox>
  );
}

function HmtSalesList() {
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  const fetchSales = useCallback(async (targetPage) => {
    try {
      setLoading(true);
      const response = await apiService.getApnaThaliPurchasePlans({
        page: targetPage,
        limit: PAGE_SIZE,
      });
      const result = response?.data;
      if (!result?.success) {
        throw new Error(result?.message || "Failed to fetch sales");
      }
      setSales(Array.isArray(result.data) ? result.data : []);
      setStats(result.stats || null);
      setPagination({
        page: result.pagination?.page ?? targetPage,
        totalPages: result.pagination?.totalPages ?? 1,
        total: result.pagination?.total ?? 0,
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to fetch sales"
      );
      setSales([]);
      setStats(null);
      setPagination({ page: 1, totalPages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSales(page);
  }, [page, fetchSales]);

  const copyOrder = async (row) => {
    try {
      await navigator.clipboard.writeText(buildOrderText(row));
      toast.success("Order copied to clipboard");
    } catch {
      toast.error("Could not copy — check browser permissions");
    }
  };

  const table = useMemo(() => {
    const columns = [
      { name: "date", align: "left", width: "140px" },
      { name: "member", align: "left", width: "200px" },
      { name: "plan", align: "left", width: "220px" },
      { name: "amount", align: "center", width: "90px" },
      { name: "address", align: "left", width: "220px" },
      { name: "invoice", align: "center", width: "100px" },
      { name: "actions", align: "center", width: "100px" },
    ];

    const rows = sales.map((row) => {
      const member = row.originalMemberId || {};
      const planName = row.plan?.name || row.hmtPlanId?.plan?.name || "—";
      const amount = row.plan?.amount ?? row.hmtPlanId?.plan?.amount;
      const invoiceUrl = getInvoiceUrl(row);
      const customerAddress = getCustomerAddress(row);
      const photoSrc = member.photo ? `${IMG_BASE}${member.photo}` : "";
      const whatsappUrl = getWhatsAppUrl(
        member.phone,
        `Hi ${member.name || ""}, this is ApnaThali regarding your HMT plan order.`
      );
      const dateRange =
        row.startDate || row.endDate
          ? `${formatShortDate(row.startDate)} – ${formatShortDate(row.endDate)}`
          : null;

      return {
        date: (
          <CellStack>
            <SoftTypography variant="button" fontWeight="medium" color="dark" component="div">
              {formatDate(row.createdAt)}
            </SoftTypography>
          </CellStack>
        ),
        member: (
          <CellStack>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftAvatar src={photoSrc} name={member.name || "?"} size="sm" />
              <SoftBox>
                <SoftTypography variant="button" fontWeight="bold" color="dark" component="div">
                  {member.name || "—"}
                </SoftTypography>
                <SoftTypography variant="caption" color="secondary" component="div">
                  {member.phone ? `+91 ${member.phone}` : "—"}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </CellStack>
        ),
        plan: (
          <CellStack>
            <SoftTypography variant="button" fontWeight="medium" color="dark" component="div">
              {planName}
            </SoftTypography>
            {row.slot && (
              <SoftTypography variant="caption" color="info" fontWeight="medium" component="div">
                {row.slot}
              </SoftTypography>
            )}
            {dateRange && (
              <SoftTypography variant="caption" color="secondary" component="div">
                {dateRange}
              </SoftTypography>
            )}
          </CellStack>
        ),
        amount: (
          <CellStack>
            <SoftTypography variant="button" fontWeight="bold" color="success" component="div">
              ₹{amount ?? "—"}
            </SoftTypography>
          </CellStack>
        ),
        address: (
          <CellStack>
            <SoftTypography
              variant="caption"
              color="secondary"
              component="div"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.4,
                maxWidth: 220,
              }}
            >
              {customerAddress}
            </SoftTypography>
          </CellStack>
        ),
        invoice: (
          <CellStack>
            {invoiceUrl ? (
              <Link
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: "0.8rem", fontWeight: 600 }}
              >
                {row.hmtPlanId?.invoiceNumber || "View PDF"}
              </Link>
            ) : (
              <SoftTypography variant="caption" color="secondary" component="div">
                —
              </SoftTypography>
            )}
          </CellStack>
        ),
        actions: (
          <CellStack>
            <SoftBox display="flex" justifyContent="center" gap={0.5}>
              <Tooltip title="Copy order">
                <IconButton size="small" onClick={() => copyOrder(row)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {whatsappUrl && (
                <Tooltip title="WhatsApp">
                  <Link
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "inline-flex", color: "#25D366" }}
                  >
                    <IconButton size="small" component="span">
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Link>
                </Tooltip>
              )}
            </SoftBox>
          </CellStack>
        ),
      };
    });

    return { columns, rows };
  }, [sales]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {stats && (
          <SoftBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MiniStatisticsCard
                  title={{ text: "Total Purchases" }}
                  count={stats.totalPurchases ?? 0}
                  percentage={{ color: "info", text: "" }}
                  icon={{ color: "info", component: "shopping_cart" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MiniStatisticsCard
                  title={{ text: "Total Revenue" }}
                  count={`₹${stats.totalAmount ?? 0}`}
                  percentage={{ color: "success", text: "" }}
                  icon={{ color: "success", component: "payments" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MiniStatisticsCard
                  title={{ text: "Total Thalis" }}
                  count={stats.totalThalis ?? 0}
                  percentage={{ color: "warning", text: "" }}
                  icon={{ color: "warning", component: "restaurant" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MiniStatisticsCard
                  title={{ text: stats.currentMonthLabel || "This Month" }}
                  count={`₹${stats.currentMonthRevenue ?? 0}`}
                  percentage={{ color: "info", text: "" }}
                  icon={{ color: "info", component: "calendar_today" }}
                />
              </Grid>
            </Grid>
          </SoftBox>
        )}

        <Card>
          <SoftBox
            px={3}
            py={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            borderBottom={({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`
            }
          >
            <SoftBox>
              <SoftTypography variant="h6" fontWeight="bold" color="dark">
                Sales list
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                {pagination.total} purchase{pagination.total === 1 ? "" : "s"}
              </SoftTypography>
            </SoftBox>
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchSales(page)} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </SoftBox>

          <SoftBox p={2}>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                "& tbody td": { verticalAlign: "middle" },
              }}
            >
              {loading ? (
                <SoftBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={32} />
                </SoftBox>
              ) : table.rows.length === 0 ? (
                <SoftBox py={6} textAlign="center">
                  <SoftTypography variant="body2" color="text">
                    No HMT sales found.
                  </SoftTypography>
                </SoftBox>
              ) : (
                <Table columns={table.columns} rows={table.rows} />
              )}
            </SoftBox>

            <SoftBox
              mt={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <SoftTypography variant="caption" color="text">
                Page {pagination.page} of {pagination.totalPages} · {pagination.total} sale
                {pagination.total === 1 ? "" : "s"}
              </SoftTypography>
              <SoftBox display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page >= pagination.totalPages || loading}
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, pagination.totalPages || 1))
                  }
                >
                  Next
                </Button>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default HmtSalesList;
